import { Dispatch, SetStateAction, useState } from "react";

import { Autocomplete, Box, CircularProgress, IconButton, InputAdornment, Stack, TextField, Typography } from "@mui/material";

// Componentes/
import MessageError from "../MessageError";

// Iconos
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

// Servicios y libreriras
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import { FieldErrors, FieldValues, UseFormGetValues, UseFormRegister } from "react-hook-form";
import MaskedTextField from "Components/TextMask";
import { useSettings } from "Context/Settings/useSettings";
// import MaskedInputText from "Components/MaskInputText";

interface OneStepsProps {
    errors: FieldErrors<FieldValues>
    t: any
    register: UseFormRegister<FieldValues>
    getValues: UseFormGetValues<FieldValues>
    data: any
    loaderSendEmail: boolean
    setLoaderSendEmail: Dispatch<SetStateAction<boolean>>
    roleList: any[]

    messagesError?: any[]
    roleSelected: any
    setRoleSelected: Dispatch<SetStateAction<any>>
    setMessages?: Dispatch<SetStateAction<any>>

    regexPassword: RegExp
    regexEmail: RegExp

    user: any
    setUser: Dispatch<SetStateAction<any>>
}

const FirstStep = (props: OneStepsProps) => {
    const {
        errors,
        t,
        register,
        getValues,
        data,
        loaderSendEmail,
        roleSelected,
        messagesError,
        setMessages,
        setRoleSelected,
        setLoaderSendEmail,
        roleList,
        regexPassword,
        user,
        setUser
    } = props;
    const [showPassword, setShowPassword] = useState(false);
    const settings = useSettings()

    // Metodo para enviar correoo a la cuenta para restablecer la contraseña
    const resetPasswordByEmail = async () => {
        setLoaderSendEmail(true)
        sendPasswordResetEmail(getAuth(), getValues("email"))
            .then(() => {
                setLoaderSendEmail(false)
                settings.setSnackBar({
                    message: "Mensaje enviado correctamente para reestablecer la contraseña",
                    type: "success",
                    open: true
                })
            })
            .catch((error) => {
                setLoaderSendEmail(false)
                settings.setSnackBar({
                    message: error?.response?.data?.message ?? "Error al enviar el correo, intente nuevamente o contacte con soporte",
                    type: "error",
                    open: true
                })
            });
    }

    return (
        <Box>
            <h5>Informacion basica de la cuenta</h5>
            <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField
                    autoFocus
                    label={t("userControl.text_10")}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    helperText={<MessageError message={errors?.name?.message?.toString() ?? ""} />}
                    {...register("name", {
                        required: {
                            value: true,
                            message: t("userControl.text_11")
                        }
                    })} />

                <TextField
                    label={t("userControl.text_12")}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    helperText={<MessageError message={errors?.lastName?.message?.toString() ?? ""} />}
                    {...register("lastName", {
                        required: { value: true, message: t("userControl.text_11") }
                    })} />

                <Box>
                    <MaskedTextField
                        label={t("userControl.text_14")}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={user?.document ?? ""}
                        onChange={(e: any) => setUser((prev: any) => ({ ...prev, document: e.target.value }))}
                        mask="00000000-0"
                    />
                    <MessageError
                        message={messagesError?.find((item) => item.id === "document")?.message ?? ""}
                    />
                </Box>

                <Box>
                    <MaskedTextField
                        label={t("userControl.text_15")}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={user?.phone ?? ""}
                        onChange={(e: any) => setUser((prev: any) => ({ ...prev, phone: e.target.value }))}
                        mask="0000-0000"
                    />
                    <MessageError
                        message={messagesError?.find((item) => item.id === "phone")?.message ?? ""}
                    />
                </Box>
            </Stack>

            <Stack spacing={2} sx={{ mt: 3 }}>
                <TextField
                    label={t("perfil.text_2")}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    helperText={<MessageError message={errors?.email?.message?.toString() ?? ""} />}
                    {...register("email", {
                        required: {
                            value: true,
                            message: t("userControl.text_11")
                        },
                        pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                            message: t("userControl.text_17")
                        }
                    })} />

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1
                    }}
                >
                    <TextField
                        label={t("userControl.text_18")}
                        fullWidth
                        type={showPassword ? "text" : "password"}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                    >
                                        {showPassword ? (
                                            <VisibilityOffIcon sx={{ color: "primary.dark" }} />
                                        ) : (
                                            <VisibilityIcon sx={{ color: "primary.dark" }} />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        helperText={<MessageError message={errors?.password?.message?.toString() ?? ""} />}
                        {...register("password", {
                            required: {
                                value: data === null,
                                message: t("userControl.text_11")
                            },
                            minLength: {
                                value: getValues("password") !== "" ? 8 : 0,
                                message: t("userControl.text_19")
                            },
                            pattern: {
                                value: getValues("password") !== "" ? regexPassword : /.*/,
                                message: t("userControl.text_20")
                            }
                        })} />

                    {data !== null && (
                        <>
                            {loaderSendEmail ? (
                                <CircularProgress size={24} />
                            ) : (
                                <IconButton
                                    sx={{ flexShrink: 0 }}
                                    aria-label="Enviar correo de restablecimiento"
                                    onClick={resetPasswordByEmail}
                                >
                                    <ForwardToInboxIcon />
                                </IconButton>
                            )}
                        </>
                    )}
                </Box>
            </Stack>
            <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 1 }}>
                    {"Seleccione el rol"}
                </Typography>
                <Autocomplete
                    value={roleSelected ?? null}
                    onChange={(_, newValue) => {
                        if (setRoleSelected) setRoleSelected(newValue);
                        if (setMessages)
                            setMessages(
                                (prevState: any) => prevState?.filter((item: any) => item.id !== "role") ?? []
                            );
                    }}
                    options={Array.isArray(roleList) ? roleList : []}
                    getOptionLabel={(option: any) => {
                        if (!option) return "";
                        return option?.label ?? "";
                    }}
                    isOptionEqualToValue={(option: any, value: any) => {
                        if (!option || !value) return option === value;
                        return option?.id === value?.id;
                    }}
                    noOptionsText="Sin opciones"
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            label={"Rol"}
                        />
                    )}
                />
                <MessageError message={messagesError?.find((item) => item.id === "role")?.message ?? ""} />
            </Box>
        </Box>
    );
}

export default FirstStep;